import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { vehiculosApi, type Vehiculo, type VehiculoInput } from '../api/vehiculos'
import { reportesApi, type RankingVehiculoItem } from '../api/reportes'
import { Table, type Column } from '../components/ui/Table'
import { Button } from '../components/ui/Button'
import { Modal } from '../components/ui/Modal'
import { Input } from '../components/ui/Input'
import { GenericBadge } from '../components/ui/Badge'
import { fixed, toNum } from '../utils/format'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

const vehiculoSchema = z.object({
  codigo: z.string().min(2, 'Codigo requerido'),
  tipoMaquinaId: z.string().min(1, 'Tipo requerido'),
  marca: z.string().optional(),
  modelo: z.string().optional(),
  anio: z.coerce.number().int().min(1990).max(2030).optional(),
  capacidadTanque: z.coerce.number().positive().optional(),
  horometroInicial: z.coerce.number().min(0).optional(),
  gpsImei: z.string().optional(),
})

type VehiculoForm = z.infer<typeof vehiculoSchema>

type TabView = 'lista' | 'ranking'

function lastMonthRange() {
  const hasta = new Date()
  const desde = new Date()
  desde.setDate(desde.getDate() - 30)
  const fmt = (d: Date) => d.toISOString().slice(0, 10)
  return { desde: fmt(desde), hasta: fmt(hasta) }
}

// Lookup map vehiculoId → costoPorHoraBs for the lista view
function useCostMap(): Map<string, number> {
  const { desde, hasta } = lastMonthRange()
  const { data } = useQuery({
    queryKey: ['ranking-vehiculos', desde, hasta],
    queryFn: () => reportesApi.rankingVehiculos(desde, hasta),
    staleTime: 5 * 60 * 1000,
  })
  const map = new Map<string, number>()
  data?.items.forEach((i) => map.set(i.vehiculo.id, toNum(i.costoPorHoraBs)))
  return map
}

function RankingView() {
  const { desde, hasta } = lastMonthRange()
  const { data, isLoading } = useQuery({
    queryKey: ['ranking-vehiculos', desde, hasta],
    queryFn: () => reportesApi.rankingVehiculos(desde, hasta),
    staleTime: 5 * 60 * 1000,
  })

  const sorted = [...(data?.items ?? [])].sort(
    (a, b) => toNum(b.costoPorHoraBs) - toNum(a.costoPorHoraBs),
  )

  const RANKING_COLS: Column<RankingVehiculoItem>[] = [
    {
      key: 'pos',
      header: '#',
      render: (r) => (
        <span className="text-slate-400 text-xs font-mono">
          {sorted.findIndex((i) => i.vehiculo.id === r.vehiculo.id) + 1}
        </span>
      ),
    },
    {
      key: 'vehiculo',
      header: 'Vehiculo',
      render: (r) => (
        <div>
          <p className="font-mono font-medium">{r.vehiculo.codigo}</p>
          <p className="text-xs text-slate-400">{r.vehiculo.tipoMaquina.nombre}</p>
        </div>
      ),
    },
    {
      key: 'costoPorHora',
      header: 'Bs/h',
      render: (r) => (
        <span className="tabular-nums font-semibold">
          Bs. {fixed(r.costoPorHoraBs, 1)}
        </span>
      ),
    },
    {
      key: 'litrosHora',
      header: 'L/h',
      render: (r) => <span className="tabular-nums">{fixed(r.litrosPorHora, 1)}</span>,
    },
    {
      key: 'horas',
      header: 'Horas trabajadas',
      render: (r) => <span className="tabular-nums">{fixed(r.horasTrabajadas, 0)} h</span>,
    },
    {
      key: 'totalLitros',
      header: 'Total litros',
      render: (r) => <span className="tabular-nums">{fixed(r.totalLitros, 0)} L</span>,
    },
    {
      key: 'vsEsperado',
      header: 'vs Esperado',
      render: (r) => {
        const v = toNum(r.vsEsperadoPct)
        const variant = v > 20 ? 'danger' : v > 0 ? 'warning' : 'success'
        const sign = v > 0 ? '+' : ''
        return <GenericBadge label={`${sign}${fixed(v, 1)}%`} variant={variant} />
      },
    },
  ]

  if (isLoading) return <p className="text-sm text-slate-400 animate-pulse py-4">Cargando ranking...</p>

  // TODO: handle error — fallback a tabla vacia si endpoint no responde
  return (
    <Table
      columns={RANKING_COLS}
      data={sorted}
      keyExtractor={(r) => r.vehiculo.id}
      loading={false}
      emptyMessage="Sin datos de ranking para el periodo"
    />
  )
}

export default function Vehiculos() {
  const qc = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [tab, setTab] = useState<TabView>('lista')
  const costMap = useCostMap()

  const { data, isLoading } = useQuery({
    queryKey: ['vehiculos'],
    queryFn: () => vehiculosApi.list(),
  })

  const createMutation = useMutation({
    mutationFn: (d: VehiculoInput) => vehiculosApi.create(d),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vehiculos'] })
      setModalOpen(false)
    },
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) =>
      vehiculosApi.toggleActivo(id, activo),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['vehiculos'] }),
  })

  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } =
    useForm<VehiculoForm>({ resolver: zodResolver(vehiculoSchema) })

  async function onSubmit(values: VehiculoForm) {
    await createMutation.mutateAsync(values as VehiculoInput)
    reset()
  }

  const COLUMNS: Column<Vehiculo>[] = [
    {
      key: 'codigo',
      header: 'Codigo',
      sortable: true,
      render: (r) => <span className="font-mono font-medium">{r.codigo}</span>,
    },
    { key: 'tipo', header: 'Tipo', render: (r) => r.tipoMaquina?.nombre ?? '—' },
    {
      key: 'marca',
      header: 'Marca/Modelo',
      render: (r) => [r.marca, r.modelo, r.anio].filter(Boolean).join(' ') || '—',
    },
    { key: 'capacidad', header: 'Capacidad', render: (r) => (r.capacidadTanque ? `${r.capacidadTanque} L` : '—') },
    { key: 'horometro', header: 'Horom. inicial', render: (r) => `${r.horometroInicial} h`, className: 'tabular-nums' },
    {
      key: 'bsH',
      header: 'Bs/h (30d)',
      render: (r) => {
        const v = costMap.get(r.id)
        if (v === undefined) return <span className="text-slate-400">—</span>
        return <span className="tabular-nums font-medium">Bs. {fixed(v, 1)}</span>
      },
    },
    { key: 'gps', header: 'GPS IMEI', render: (r) => r.gpsImei ?? '—' },
    {
      key: 'activo',
      header: 'Estado',
      render: (r) => (
        <GenericBadge label={r.activo ? 'Activo' : 'Inactivo'} variant={r.activo ? 'success' : 'default'} />
      ),
    },
    {
      key: 'acciones',
      header: '',
      render: (r) => (
        <button
          onClick={(e) => {
            e.stopPropagation()
            toggleMutation.mutate({ id: r.id, activo: !r.activo })
          }}
          className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
        >
          {r.activo ? 'Desactivar' : 'Activar'}
        </button>
      ),
    },
  ]

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Vehiculos / Maquinaria</h1>
        <Button size="sm" onClick={() => { reset(); setModalOpen(true) }}>+ Nuevo vehiculo</Button>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-700">
        {(['lista', 'ranking'] as TabView[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={[
              'px-4 py-2 text-sm font-medium capitalize transition-colors',
              tab === t
                ? 'border-b-2 border-primary text-primary'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300',
            ].join(' ')}
          >
            {t === 'lista' ? 'Vista lista' : 'Vista ranking'}
          </button>
        ))}
      </div>

      {tab === 'lista' ? (
        <Table
          columns={COLUMNS}
          data={data?.items ?? []}
          keyExtractor={(r) => r.id}
          loading={isLoading}
          emptyMessage="Sin vehiculos registrados"
        />
      ) : (
        <RankingView />
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Nuevo vehiculo" size="lg">
        <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-2 gap-4">
          <Input {...register('codigo')} label="Codigo" placeholder="TH-EX 02" error={errors.codigo?.message} />
          <Input {...register('tipoMaquinaId')} label="ID Tipo maquina" placeholder="cuid..." error={errors.tipoMaquinaId?.message} />
          <Input {...register('marca')} label="Marca" placeholder="Caterpillar" />
          <Input {...register('modelo')} label="Modelo" placeholder="320D" />
          <Input {...register('anio')} type="number" label="Anio" placeholder="2020" />
          <Input {...register('capacidadTanque')} type="number" label="Capacidad tanque (L)" placeholder="500" />
          <Input {...register('horometroInicial')} type="number" label="Horometro inicial (h)" placeholder="0" />
          <Input {...register('gpsImei')} label="GPS IMEI" placeholder="opcional" />
          {createMutation.isError && (
            <p className="col-span-2 text-xs text-danger">Error al guardar. Verifique los datos.</p>
          )}
          <div className="col-span-2 flex gap-3 justify-end pt-2">
            <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={isSubmitting}>Guardar</Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
